import React from "react";
import { INPUT_TYPES } from "@miplatform/constants";
import { Box } from "@mui/material";
import { useTheme } from "@mui/system";
import { MiControlledAutoComplete } from "../MiControlledAutoComplete/MiControlledAutoComplete";
import { MiControlledCheckBox } from "../MiControlledCheckBox/MiControlledCheckBox";
import { MiControlledNumericInput } from "../MiControlledNumericInput/MiControlledNumericInput";
import { MiControlledRadioGroup } from "../MiControlledRadioGroup/MiControlledRadioGroup";
import { MiControlledSelectBox } from "../MiControlledSelectBox/MiControlledSelectBox";
import { MiControlledSwitch } from "../MiControlledSwitch/MiControlledSwitch";
import { MiControlledTextField } from "../MiControlledTextField/MiControlledTextField";
import { MiControlledTimerPicker } from "../MiControlledTimePicker/MiControlledTimerPicker";
import { MiControlledInputMultiSelectBox } from "../MiInputMultiSelectBox/MiControlledInputMultiSelectBox";

export const MiControlledInput = ({ item, formControl }) => {
  const theme = useTheme();

  switch (item.fieldType) {
    case INPUT_TYPES.SELECT:
      return (
        <MiControlledSelectBox
          name={item.name}
          fieldType={item.fieldType}
          formControl={formControl}
          label={item.label}
          value={item.value}
          minwidth={item.minWidth}
          options={item.options || []}
          disabled={item.disabled}
          readOnly={item.readOnly}
          fullWidth={item.fullWidth}
          required={item.required}
          size={item.size}
          disableNone={!item.selectNone}
          style={item.style}
          handleChange={item.setValue}
          {...item}
        />
      );
    case INPUT_TYPES.AUTOCOMPLETE:
      return (
        <MiControlledAutoComplete
          name={item.name}
          formControl={formControl}
          getOptionLabel={item.optionLabel}
          label={item.label}
          inputValue={item.inputValue}
          onInputChange={item.inputChange}
          variant={item.variant || "standard"}
          options={item.options}
          required={item.required}
          disabled={item.disabled}
          readOnly={item.readOnly}
          freeSolo={item.freeSolo || false}
          filterOptions={item.filterOptions}
          renderOption={item.renderOption}
          renderInput={item.renderInput}
          minwidth={item.minWidth}
          forcePopupIcon={item.forcePopupIcon}
          disablePortal={item.disablePortal ?? false}
          textFieldOnChange={item.textFieldOnChange}
          value={item.value}
          disableClearable={item.disableClearable}
          textFieldOnFocus={item.textFieldOnFocus}
          sx={item.style}
          {...item}
        />
      );
    case INPUT_TYPES.CHECKBOX:
      return (
        <MiControlledCheckBox
          name={item.name}
          checked={item.value}
          formControl={formControl}
          label={item.label}
          style={item.style}
          disabled={item.disabled}
          readOnly={item.readOnly}
          icon={item.icon}
          checkedIcon={item.checkedIcon}
          handleChange={item.setValue}
          labelPlacement={item.labelPlacement}
          {...item}
        />
      );
    case INPUT_TYPES.SWITCH:
      return (
        <MiControlledSwitch
          name={item.name}
          control={formControl}
          label={item.label}
          checked={item.value}
          disabled={item.disabled}
          readOnly={item.readOnly}
          value={item.value}
          handleChange={item.setValue}
          {...item}
        />
      );
    case INPUT_TYPES.EMPTY_FIELD:
      return <Box />;
    case INPUT_TYPES.CUSTOM:
      return React.cloneElement(item.customComponent, { ...item });
    case INPUT_TYPES.RADIO_GROUP:
      return (
        <MiControlledRadioGroup
          name={item.name}
          control={formControl}
          label={item.label}
          helperText={item.helperText}
          rules={item.rules}
          options={item.options}
          disabled={item.disabled}
          readOnly={item.readOnly}
          row={item.row}
          radioSize={item.radioSize}
          sx={item.sx}
          handleChange={item.setValue}
        />
      );
    case INPUT_TYPES.MULTI_SELECT:
      return (
        <MiControlledInputMultiSelectBox
          value={item.value}
          labelText={item.label}
          menuItems={item?.options}
          disableNone={!item.selectNone}
          readOnly={item.readOnly}
          disabled={item.disabled}
          required={item.required}
          multiline={item.multiline}
          error={!!item.error}
          minWidth={item.minWidth}
          marginBottom={item.marginBottom}
          marginRight={item.marginRight}
          helperText={item.helperText}
          placeholder={item.placeholder}
          rows={item.rows}
          type={item.fieldType}
          InputLabelProps={{ style: { color: theme.palette.grey.darkAccent } }}
          InputProps={item.InputProps}
          control={formControl}
          {...item}
        />
      );
    case INPUT_TYPES.CURRENCY: {
      return (
        <MiControlledNumericInput
          thousandSeparator
          decimalScale={2}
          fixedDecimalScale
          allowNegative={item.allowNegative || false}
          allowLeadingZeros={false}
          prefix="$"
          suffix={item.suffix}
          name={item.name}
          disabled={item.disabled}
          formControl={formControl}
          onValueChange={(values) => item.setValue(values.value)}
          width={item.width}
          required={item.required}
          padding={item.padding}
          minWidth={item.minWidth}
          flexGrow={item.flexGrow}
          label={item.label}
          placeholder={item.placeholder}
          value={item.value}
          readOnly={item.readOnly}
        />
      );
    }
    case INPUT_TYPES.NUMERIC: {
      return (
        <MiControlledNumericInput
          thousandSeparator={item.thousandSeparator}
          decimalScale={item.decimalScale}
          fixedDecimalScale={item.fixedDecimalScale}
          allowNegative={item.allowNegative}
          allowLeadingZeros={item.allowLeadingZeros}
          prefix={item.prefix}
          suffix={item.suffix}
          name={item.name}
          disabled={item.disabled}
          formControl={formControl}
          onValueChange={(values) => item.setValue(values.floatValue)}
          width={item.width}
          required={item.required}
          padding={item.padding}
          minWidth={item.minWidth}
          flexGrow={item.flexGrow}
          label={item.label}
          placeholder={item.placeholder}
          value={item.value}
          readOnly={item.readOnly}
          InputProps={item.InputProps}
          sx={item.sx}
          error={item.error}
          helperText={item.helperText}
        />
      );
    }
    case INPUT_TYPES.TIME_PICKER:
      return (
        <MiControlledTimerPicker
          name={item.name}
          label={item.label}
          value={item.value}
          onChange={item.setValue}
          disabled={item.disabled}
          readOnly={item.readOnly}
          formControl={formControl}
          minWidth={item.minWidth}
          fullWidth={item.fullWidth}
          ampm={item.ampm}
          strictInterval={item.strictInterval}
          {...item}
        />
      );
    case INPUT_TYPES.TEXTBOX:
    default:
      return (
        <MiControlledTextField
          name={item.name}
          disabled={item.disabled}
          readOnly={item.readOnly}
          formControl={formControl}
          valueModifier={item.valueModifier}
          handleChange={item.setValue}
          type={item.fieldType}
          {...item}
        />
      );
  }
};
